// TIP-493 (MV Stage 4b) — display pricing for the "Buy a Web Profile" checkout.
//
// Amounts are in AED ("Dh") and mirror the Stripe AED prices the backend charges
// (the PAYG web profile + the recurring membership). The AUTHORITATIVE totals
// still come from Stripe at checkout (create_payment.extra_details, which
// includes automatic_tax). These constants only drive the comparison cards and
// the summary panel BEFORE the Stripe round-trip, so the user sees the rate and
// the member savings up front. Keep them in sync with the minted Stripe prices.

export const CURRENCY = "Dh";
export const MEMBER_DISCOUNT_RATE = 0.1; // 10% off the web profile for members
export const PROFILE_WINDOW_DAYS = 30;

export const WEB_PROFILE_STANDARD = 99; // Dh 99 / 30 days, one-time
export const WEB_PROFILE_MEMBER = Number(
  (WEB_PROFILE_STANDARD * (1 - MEMBER_DISCOUNT_RATE)).toFixed(2)
); // 89.10
export const WEB_PROFILE_SAVINGS = Number(
  (WEB_PROFILE_STANDARD - WEB_PROFILE_MEMBER).toFixed(2)
); // 9.90

export const MEMBERSHIP_ANNUAL = 999; // Dh 999 / year (two months free)
// TIP-526 — must mirror the Stripe AED price (STRIPE_PRICE_ID_MEMBERSHIP_MONTHLY
// = AED 99.00); a 99.90 here made the displayed total differ from the charge.
export const MEMBERSHIP_MONTHLY = 99; // Dh 99 / month

export const PLAN_YEARLY = "yearly";
export const PLAN_MONTHLY = "monthly";

export const formatAed = (n) =>
  `${CURRENCY} ${Number(n).toLocaleString("en-AE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const membershipPrice = (plan) =>
  plan === PLAN_YEARLY ? MEMBERSHIP_ANNUAL : MEMBERSHIP_MONTHLY;

// UAE VAT. The Stripe AED prices are tax-EXCLUSIVE, so Stripe adds 5% on top at
// checkout — the summary mirrors that (VAT line + VAT-inclusive total) so the
// displayed total matches the amount actually charged.
export const VAT_RATE = 0.05;
// TIP-526 — compute in integer fils so half-up rounding matches Stripe's
// (188.10 * 0.05 in floats lands on 9.40499… → 9.40, but Stripe charges 9.41).
const withTax = (lines, subtotal, renewalNote = null) => {
  const subtotalFils = Math.round(subtotal * 100);
  const vatFils = Math.round(subtotalFils * VAT_RATE);
  return {
    lines,
    subtotal: subtotalFils / 100,
    vat: vatFils / 100,
    total: (subtotalFils + vatFils) / 100,
    renewalNote,
  };
};

// Right-hand summary line items + total for the chosen option.
//   mode 'payg'   → standard one-time web profile only.
//   mode 'bundle' → membership (plan) + web profile at the member rate.
//   mode 'member' → an existing member buying at the member rate (no membership line).
export const buildWebProfileSummary = ({ mode, plan = PLAN_YEARLY }) => {
  const profileLabel = `Web Profile · ${PROFILE_WINDOW_DAYS} days`;

  if (mode === "bundle") {
    const membership = membershipPrice(plan);
    return withTax(
      [
        {
          key: "membership",
          label: plan === PLAN_YEARLY ? "Annual Membership" : "Monthly Membership",
          value: membership,
        },
        {
          key: "profile",
          label: profileLabel,
          sublabel: "Member rate",
          value: WEB_PROFILE_MEMBER,
          strike: WEB_PROFILE_STANDARD,
        },
        {
          key: "savings",
          label: "Member savings",
          value: -WEB_PROFILE_SAVINGS,
          savings: true,
        },
      ],
      membership + WEB_PROFILE_MEMBER,
      plan === PLAN_YEARLY
        ? "Membership renews annually. Cancel any time at the end of the term."
        : "Membership renews monthly. Cancel any time at the end of the term."
    );
  }

  if (mode === "member") {
    return withTax(
      [
        {
          key: "profile",
          label: profileLabel,
          sublabel: "Member rate",
          value: WEB_PROFILE_MEMBER,
          strike: WEB_PROFILE_STANDARD,
        },
      ],
      WEB_PROFILE_MEMBER
    );
  }

  // payg (default)
  return withTax(
    [{ key: "profile", label: profileLabel, value: WEB_PROFILE_STANDARD }],
    WEB_PROFILE_STANDARD
  );
};
